import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, X } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { BrandLockup } from "../components/BrandLockup";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";

export default function SignIn() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setError("");
        setBusy(true);
        const res = await login({ email, password });
        setBusy(false);
        if (res.ok) {
            toast.success("Welcome back.");
            navigate("/dashboard");
        } else {
            setError(res.error);
        }
    }

    return (
        <div className="min-h-screen rem-watercolor-bg flex items-center justify-center px-6 py-16 relative" data-testid="signin-page">
            <Link
                to="/"
                className="absolute top-6 right-6 z-10 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/80 hover:bg-white border border-[#E5E1D8] text-xs uppercase tracking-[0.22em] text-[#5C5A59] hover:text-[#4A5A95] transition-all shadow-rem-soft"
                data-testid="signin-close"
                aria-label="Close — back to home"
            >
                <X size={14} weight="bold" />
                Close
            </Link>
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md"
            >
                <Link to="/" className="flex justify-center mb-10" data-testid="signin-back-home">
                    <BrandLockup size="md" />
                </Link>

                <div className="rem-greeting-card rounded-[2rem] p-8 sm:p-10">
                    <h1 className="font-serif text-3xl text-[#2D2B2A] text-center leading-tight" data-testid="signin-heading">
                        Good morning. <em className="italic text-[#4A5A95]">Let's reminisce.</em>
                    </h1>
                    <p className="mt-3 text-center text-[#5C5A59] text-sm">Sign in to continue your family's profile.</p>

                    <form onSubmit={submit} className="mt-8 space-y-3" data-testid="signin-form">
                        <input
                            type="email"
                            required
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-5 py-3.5 rounded-full text-sm bg-white text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all"
                            data-testid="signin-email"
                        />
                        <input
                            type="password"
                            required
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-5 py-3.5 rounded-full text-sm bg-white text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all"
                            data-testid="signin-password"
                        />

                        {error && (
                            <p className="text-sm text-red-600 px-3" data-testid="signin-error">{error}</p>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            className="rem-start-button w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm transition-all disabled:opacity-60 mt-2"
                            data-testid="signin-submit"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                            <ArrowRight weight="bold" size={16} />
                        </button>
                    </form>

                    <p className="mt-7 text-center text-sm text-[#8A8786]">
                        New here?{" "}
                        <Link to="/sign-up" className="text-[#6B7DB8] hover:underline" data-testid="signin-to-signup">Create an account</Link>
                    </p>
                </div>
            </motion.div>
        </div>
    );
}
